import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@dealstack/frontend/shared/components/Alert/Alert';
import { Expandable } from '@dealstack/frontend/shared/components/Expandable/Expandable';
import { Button } from '@dealstack/frontend/shared/components/fundament/Button/Button';
import { GenericBooleanStatus } from '@dealstack/frontend/shared/components/GenericStatus/variants/GenericBooleanStatus/GenericBooleanStatus';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { SectionHeader } from '@dealstack/frontend/shared/components/SectionHeader/SectionHeader';
import { TagPair, TagText } from '@dealstack/frontend/shared/components/TagPair/TagPair';

import { CustomDetails } from '@dealstack/frontend/admin/pages/Manage/MIP/ManageMip/CustomDetails/CustomDetails';
import { Deal } from '@dealstack/frontend/admin/pages/Manage/MIP/ManageMip/Deal/Deal';
import { Investments } from '@dealstack/frontend/admin/pages/Manage/MIP/ManageMip/Investments/Investments';
import { Investors } from '@dealstack/frontend/admin/pages/Manage/MIP/ManageMip/Investors/Investors';
import { LoansManagement } from '@dealstack/frontend/admin/pages/Manage/MIP/ManageMip/LoansManagement/LoansManagement';
import { MIPArchiveButton } from '@dealstack/frontend/admin/pages/Manage/MIP/ManageMip/MIPArchiveButton/MIPArchiveButton';
import { SimpleDocumentWorkflowBatch } from '@dealstack/frontend/admin/pages/Manage/MIP/ManageMip/SimpleDocumentWorkflowBatch/SimpleDocumentWorkflowBatch';
import { useManageMip } from '@dealstack/frontend/admin/pages/Manage/MIP/ManageMip/useManageMip';
import { GODMODE_ROOT_ROUTES } from '@dealstack/frontend/admin/pages/Routing/Routing';
import { Flex } from '@mantine/core';
import { memo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

interface Props {
	className?: string;
}

const ManageMip = ({ className }: Props) => {
	const navigate = useNavigate();
	const manageMipController = useManageMip();
	const {
		mipData,
		isAnyInvestorInvited,
		isDataLoading,
		refetchMipData,
		onLockButtonSubmit,
		onKycButtonSubmit,
	} = manageMipController;

	return (
		<div className={className}>
			{isDataLoading ? <InlineLoader /> : null}
			<Button
				onClick={() => {
					navigate(GODMODE_ROOT_ROUTES.MANAGE_MIP);
				}}
				variant="project"
			>
				Back to list
			</Button>
			<Flex justify="space-between" mb="1rem">
				<SectionHeader title="Investment programme" />
			</Flex>
			<GridBox>
				<TagPair title="Investment programme name">
					<TagText>{mipData?.programmeName}</TagText>
				</TagPair>
				<TagPair title="Entity name">
					<TagText>{mipData?.entity?.displayName}</TagText>
				</TagPair>
				<TagPair title="MIP currency">
					<TagText>{mipData?.currency}</TagText>
				</TagPair>
				<TagPair title="State">
					<TagText>{mipData?.state}</TagText>
				</TagPair>
				<TagPair title="Is locked">
					<GenericBooleanStatus value={mipData?.isLocked} />
				</TagPair>
				<Button variant="outlined" onClick={onLockButtonSubmit}>
					{mipData?.isLocked ? 'Unlock IP' : 'Lock IP'}
				</Button>
				<TagPair title="Is Archived">
					<GenericBooleanStatus value={mipData?.deleted} />
				</TagPair>
				<TagPair title="Is KYC Required">
					<GenericBooleanStatus value={mipData?.isKycRequired} />
				</TagPair>
				<Button variant="outlined" onClick={onKycButtonSubmit}>
					Change Programme KYC requirement
				</Button>
				{isAnyInvestorInvited && (
					<div
						style={{
							flexBasis: '100%',
						}}
					>
						<Alert icon="information-filled" state="warning" size="m">
							<AlertTitle>WARNING</AlertTitle>
							<AlertDescription>
								You shouldn't change KYC status when some investors have been in
								invited state.
							</AlertDescription>
						</Alert>
					</div>
				)}
			</GridBox>

			<Expandable title="Deal of investment programme" preventRenderWhenHidden>
				<Deal refetchMipData={refetchMipData} />
			</Expandable>
			<Expandable title="Investors" preventRenderWhenHidden>
				{mipData ? <Investors mipId={mipData?.id} /> : <span>Loading</span>}
			</Expandable>
			<Expandable title="Investments (InvestmentWorkflow)" preventRenderWhenHidden>
				{mipData ? <Investments mipId={mipData?.id} /> : <span>Loading</span>}
			</Expandable>
			<Expandable title="Simple Document Workflow Batch" preventRenderWhenHidden>
				{mipData ? (
					<SimpleDocumentWorkflowBatch mipId={mipData?.id} dealId={mipData?.dealId} />
				) : (
					<span>Loading</span>
				)}
			</Expandable>
			<Expandable title="Loans Management" preventRenderWhenHidden>
				{mipData ? <LoansManagement mipId={mipData?.id} /> : <span>Loading</span>}
			</Expandable>
			<Expandable title="Custom details" preventRenderWhenHidden>
				<CustomDetails />
			</Expandable>
			<ButtonsWrapper>
				{!!mipData && (
					<MIPArchiveButton
						isDealArchived={mipData.deal?.deleted}
						isMIPArchived={mipData.deleted}
						programmeName={mipData.programmeName}
						mipId={mipData.id}
						refetchMipData={refetchMipData}
					/>
				)}
				<Link to={`${GODMODE_ROOT_ROUTES.MANAGE_MIP}/deal/${mipData?.dealId}`}>
					<Button variant="secondary">Go to parent deal</Button>
				</Link>
			</ButtonsWrapper>
		</div>
	);
};

const GridBox = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 1rem;
`;

const ButtonsWrapper = styled.div`
	display: flex;
	align-items: flex-start;

	& > * {
		margin-right: 2rem;
	}
`;

const Memoized = memo(ManageMip);
const Styled = styled(Memoized)`
	position: relative;

	& > ${Button}, & > ${GridBox}, & > ${Expandable}, & > ${ButtonsWrapper} {
		margin-bottom: 1rem;
	}
`;

export { Styled as ManageMip };
